import { Input } from '../ui/Input.jsx';
import { Select } from '../ui/Select.jsx';
import { Switch } from '../ui/Switch.jsx';
import { TagInput } from '../ui/TagInput.jsx';
import { MultiSelect } from '../ui/MultiSelect.jsx';
import { Badge } from '../ui/Badge.jsx';
import { BYTES_PER_MB } from '../../pages/admin/settings/settingsSchema.js';

/**
 * Renders a single curated setting with the control declared in the schema.
 * Presentational: the parent owns the draft value and persistence.
 *
 * @param value  current draft value (already in the unit the control edits)
 * @param onChange(value)
 */
export function SettingField({ field, setting, value, onChange, disabled, roleOptions = [], modelOptions = {} }) {
  const { control, label, help, unit } = field;

  const labelEl = (
    <span className="flex items-center gap-2 text-sm font-medium text-ink">
      {label}
    </span>
  );

  const wrap = (node) => (
    <div className="flex flex-col gap-1.5">
      {control !== 'toggle' && labelEl}
      {node}
      {help && <span className="text-xs text-ink-muted">{help}</span>}
    </div>
  );

  if (control === 'toggle') {
    return (
      <div className="flex items-start justify-between gap-4 py-1">
        <div className="min-w-0">
          {labelEl}
          {help && <span className="text-xs text-ink-muted">{help}</span>}
        </div>
        <Switch checked={Boolean(value)} disabled={disabled} onChange={onChange} label={label} />
      </div>
    );
  }

  if (control === 'password') {
    return wrap(
      <div className="flex items-center gap-2">
        <Input
          type="password"
          placeholder={setting && setting.hasValue ? 'Inserisci una nuova chiave per sostituirla' : 'Non configurata'}
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="w-full max-w-md"
        />
        {setting && setting.hasValue
          ? <Badge tone="success">Configurata</Badge>
          : <Badge tone="neutral">Non configurata</Badge>}
      </div>
    );
  }

  if (control === 'select') {
    return wrap(
      <Select options={field.options} value={String(value ?? '')} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="w-full max-w-xs" />
    );
  }

  if (control === 'modelSelect') {
    const opts = modelOptions[field.modelKind] || [];
    // Ensure the current value is selectable even if not in the catalog.
    const withCurrent = value && !opts.some((o) => o.value === value)
      ? [{ value, label: `${value} (non in catalogo)` }, ...opts]
      : opts;
    if (withCurrent.length === 0) {
      return wrap(<Input value={value ?? ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="w-full max-w-md" />);
    }
    return wrap(
      <Select options={withCurrent} value={String(value ?? '')} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="w-full max-w-sm" />
    );
  }

  if (control === 'rolesMulti') {
    return wrap(
      <MultiSelect options={roleOptions} value={Array.isArray(value) ? value : []} onChange={onChange} placeholder="Seleziona ruoli..." />
    );
  }

  if (control === 'tags' || control === 'tagsNum') {
    const asStrings = (Array.isArray(value) ? value : []).map(String);
    return wrap(
      <TagInput
        value={asStrings}
        lowercase={Boolean(field.lowercase)}
        onChange={(arr) => onChange(control === 'tagsNum' ? arr.map(Number).filter((n) => Number.isFinite(n)) : arr)}
      />
    );
  }

  if (control === 'number' || control === 'bytes') {
    const display = control === 'bytes' ? (Number(value) || 0) / BYTES_PER_MB : value;
    return wrap(
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={field.min}
          max={field.max}
          step={field.step}
          value={display ?? ''}
          disabled={disabled}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') return onChange('');
            const n = Number(raw);
            onChange(control === 'bytes' ? Math.round(n * BYTES_PER_MB) : n);
          }}
          className="w-full max-w-[12rem]"
        />
        {unit && <span className="text-xs text-ink-muted">{unit}</span>}
      </div>
    );
  }

  // text
  return wrap(
    <Input value={value ?? ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="w-full max-w-md" />
  );
}
